<template>
  <el-dialog
    class="dialog-wrapper"
    visible
    :show-close="false"
    @close="abortCreate"
  >
    <ContentHeader
      slot="title"
      :title="
        isModalEdit
          ? $t('recipients.create.editRecipient')
          : $t('recipients.create.createRecipient')
      "
    />
    <ValidationObserver
      ref="recipientForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('recipients.create.name')"
            >
              <el-input v-model="formData.name" />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('recipients.create.laboratory')"
            >
              <el-select
                v-model="formData.laboratory"
                placeholder=""
              >
                <el-option
                  v-for="laboratory in laboratories"
                  :key="laboratory"
                  :value="laboratory"
                  :label="$t(`constants.${laboratory}`)"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('recipients.create.documentType')"
            >
              <el-select
                v-model="formData.documentType"
                placeholder=""
              >
                <el-option
                  v-for="documentType in documentTypes"
                  :key="documentType"
                  :value="documentType"
                  :label="$t(`constants.${documentType}`)"
                />
              </el-select>
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="8">
            <FormItem :label="$t('users.create.role')">
              <el-select
                v-model="formData.role"
                placeholder=""
              >
                <el-option
                  v-for="role in roles"
                  :key="role"
                  :value="role"
                  :label="role"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="4">
            <el-form-item>
              <span slot="label">{{ $t('global.add') }}</span>
              <el-button
                class="width-100"
                type="primary"
                :disabled="formData.role === ''"
                @click.prevent="addRole"
              >
                {{ $t('global.add') }}
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="24">
            <div class="table-wrapper">
              <el-table :data="assignedRoles">
                <el-table-column
                  prop="name"
                  :label="$t('recipients.create.role')"
                />
                <el-table-column
                  :label="$t('global.actions')"
                  width="100"
                >
                  <template slot-scope="scope">
                    <el-button
                      class="table-action-button"
                      icon="fas fa-times"
                      @click="removeRole(scope.row)"
                    />
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortCreate"
          >
            {{ $t('recipients.create.abort') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="invalid || assignedRoles.length === 0"
            @click.prevent="handleSubmit(createUpdateRecipient)"
          >
            {{ $t('recipients.create.save') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { rolesApi, recipientGroupsApi } from '@/api';
  import { laboratories, documentTypes } from '@/config/constants';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';

  const defaultData = {
    name: '',
    role: '',
    laboratory: '',
    documentType: documentTypes.TestReport,
    roles: [],
  };

  export default {
    name: 'CreateEditRecipient',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
    },
    props: {
      initialData: { type: Object, default: () => defaultData },
      isModalEdit: { type: Boolean, default: false },
    },
    data() {
      return {
        formData: { ...defaultData },
        roles: [],
        assignedRoles: [],
        laboratories,
        documentTypes,
      };
    },
    async created() {
      try {
        if (this.isModalEdit) {
          const { name, laboratory, roles, documentType } = this.initialData;
          this.formData = {
            ...defaultData,
            name,
            laboratory,
            documentType,
          };
          this.assignedRoles = roles.map((r) => ({
            name: r.name,
          }));
        }
        const roles = await rolesApi.index({});
        this.roles = roles.data.map((r) => r.name);
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: error.response.data.error.message,
        });
      }
    },
    methods: {
      async createUpdateRecipient() {
        const { name, laboratory, documentType } = this.formData;
        const data = {
          name,
          laboratory,
          documentType,
          roles: [...this.assignedRoles],
        };
        try {
          if (this.isModalEdit) {
            await recipientGroupsApi.patch(
              this.$route.params.recipientGroupId,
              data
            );
          } else {
            await recipientGroupsApi.create(data);
          }
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('recipients.create.success'),
          });
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      abortCreate() {
        this.$emit('dialogClose', { success: false });
      },
      addRole() {
        const { role } = this.formData;
        if (!this.assignedRoles.find((r) => r.name === role)) {
          this.assignedRoles.push({
            name: role,
          });
        }
      },
      removeRole(row) {
        const { name } = row;
        this.assignedRoles = this.assignedRoles.filter((r) => r.name !== name);
      },
    },
  };
</script>
